class CartErrorToast extends HTMLElement {
  connectedCallback() {
    this._messageEl = this.querySelector('[data-message]');
    this._closeBtn = this.querySelector('.cart-error-toast__close');

    this._closeBtn.addEventListener('click', () => this.hide());

    subscribe(PUB_SUB_EVENTS.cartError, ({ message }) => {
      const text =
        typeof message === 'string' && message.trim()
          ? message
          : window.cartStrings.error;
      this.show(text);
    });

    this._interceptCartErrors();
  }

  /**
   * Global fetch interceptor — CART ENDPOINTS ONLY (/cart/change|add|update):
   *
   *  1. Network failure on a cart call  → generic error toast
   *  2. Cart endpoint non-ok response   → specific Shopify message from body
   *                                       (covers our own cart.js and any app
   *                                       that hits the same cart endpoints)
   *
   * AAS-5288: it used to also toast on ANY fetch network failure and on any
   * 5xx — so every failing third-party call (the Salesforce chat preprod
   * endpoints, blocked analytics, the storefront graphql ping) surfaced
   * "There was an error while updating your cart" on pages that never touched
   * the cart, repeatedly on every navigation. A toast wearing the cart's
   * message must only ever speak for the cart.
   *
   * Aborted requests, and anything going to the Atomic middleware via AtomicApi,
   * are exempt — see isMiddlewareUrl below.
   *
   * The interceptor is installed once and never replaced again.
   */
  _interceptCartErrors() {
    if (window.__cartErrorInterceptorInstalled) return;
    window.__cartErrorInterceptorInstalled = true;

    const CART_ENDPOINTS = ['/cart/change', '/cart/add', '/cart/update'];
    const _nativeFetch = window.fetch;

    // Calls made through AtomicApi (the ecom middleware: newsletter signup,
    // returns, …) are exempt from every tier below. That client never throws —
    // it resolves to a normalized result and each caller renders its own inline
    // error — so a toast here is duplicate at best, and was flat-out wrong: a
    // newsletter signup timeout surfaced "There was an error while updating your
    // cart" (AAS-4273). Read lazily: window.atomicApi is injected by Liquid and
    // may land after this file.
    const isMiddlewareUrl = (urlString) => {
      const base = (window.atomicApi?.baseUrl || '').replace(/\/+$/, '');
      return !!base && urlString.startsWith(base);
    };

    window.fetch = async function (url, ...rest) {
      const urlString = typeof url === 'string' ? url : (url?.url ?? '');
      const isCartEndpoint = CART_ENDPOINTS.some((ep) => urlString.includes(ep));
      const skipToast = isMiddlewareUrl(urlString);

      let response;

      // ── Tier 1: Network failure on a cart call ───────────────────────────
      try {
        response = await _nativeFetch(url, ...rest);
      } catch (networkError) {
        // An abort is deliberate — a client-side timeout, a cancelled request, a
        // page navigation — not an outage worth shouting about.
        const isAbort = networkError?.name === 'AbortError';
        if (isCartEndpoint && !skipToast && !isAbort) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'fetch-interceptor',
            message: window.cartStrings?.error,
          });
        }
        throw networkError;
      }

      if (response.ok) return response;

      // ── Tier 2: Cart endpoint error (4xx / 5xx) ──────────────────────────
      if (isCartEndpoint) {
        response
          .clone()
          .json()
          .then((data) => {
            const message =
              data.message || data.description || window.cartStrings?.error;
            publish(PUB_SUB_EVENTS.cartError, { source: 'fetch-interceptor', message });
          })
          .catch(() => {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'fetch-interceptor',
              message: window.cartStrings?.error,
            });
          });
      }

      return response;
    };
  }

  show(message) {
    const TOAST_MS = 5000;
    this._messageEl.textContent = message;
    this.removeAttribute('hidden');
    // Force reflow so the CSS transition fires correctly after removing hidden
    this.offsetHeight; // eslint-disable-line no-unused-expressions
    this.classList.add('is-visible');

    clearTimeout(this._timer);
    this._timer = setTimeout(() => this.hide(), TOAST_MS);
    // Auto-hide countdown sweep over the black pill (AAS-1166).
    window.toastProgress?.(this, TOAST_MS, '.cart-error-toast__inner');
  }

  hide() {
    this.classList.remove('is-visible');
    clearTimeout(this._timer);

    this.addEventListener(
      'transitionend',
      () => this.setAttribute('hidden', ''),
      { once: true }
    );
  }
}

customElements.define('cart-error-toast', CartErrorToast);
